import { Injectable, signal, computed, inject } from '@angular/core';
import { ConfigurationService, DreambooksApiConfig } from '@dreambooks/api';
import { RendererService } from '@dreambooks/renderer';
import { environment } from '../../../environments/environment';
import { AppState, PartnerInfo, Country } from '../models';
import { Category, ProductInfo, FinishOptionInfo, FinishRuleInfo } from '../models';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  private readonly dreambooksConfiguration = inject(ConfigurationService);
  private readonly dreambooksApiConfig = inject(DreambooksApiConfig);
  private readonly rendererService = inject(RendererService);

  // Signals for reactive state management
  private readonly _state = signal<AppState>(AppState.LOADING);
  private readonly _previousState = signal<AppState | null>(null);
  private readonly _countries = signal<Country[]>([]);
  private readonly _locale = signal<string>('pt_PT');
  private readonly _serverPing = signal<boolean>(true);
  private readonly _partner = signal<PartnerInfo | null>(null);
  private readonly _uploading = signal<boolean>(false);
  private readonly _categories = signal<Category[]>([]);
  private readonly _searchResults = signal<ProductInfo[]>([]);
  private readonly _loadingModalVisible = signal<boolean>(false);
  private readonly _lockModalVisible = signal<boolean>(false);
  private readonly _isModuleEditorLoaded = signal<boolean>(false);
  private readonly _selectedCategory = signal<string | null>(null);
  private readonly _selectedProduct = signal<ProductInfo | null>(null);
  /** Manual hide-prices toggle. Persisted in localStorage so a refresh keeps
   *  it (same behavior as the price-inflation factor); still reset on logout
   *  so it can't leak into another user's session. */
  private static readonly HIDE_PRICES_KEY = 'hidePricesToggle';
  private readonly _hidePrices = signal<boolean>(AppStateService.readStoredHidePrices());

  private static readStoredHidePrices(): boolean {
    try {
      return localStorage.getItem(AppStateService.HIDE_PRICES_KEY) === '1';
    } catch {
      return false;
    }
  }
  // Global finish options cache - shared across all product contexts
  private readonly _globalFinishOptionsCache = signal<Map<number, FinishOptionInfo[]>>(new Map());
  private readonly _globalFinishRulesCache = signal<Map<number, FinishRuleInfo[]>>(new Map());
  private readonly _finishOptionsLoadingState = signal<Map<number, 'loading' | 'loaded' | 'error'>>(new Map());

  // Readonly signals for external access
  readonly state = this._state.asReadonly();
  readonly previousState = this._previousState.asReadonly();
  readonly countries = this._countries.asReadonly();
  readonly locale = this._locale.asReadonly();
  readonly serverPing = this._serverPing.asReadonly();
  readonly partner = this._partner.asReadonly();
  readonly uploading = this._uploading.asReadonly();
  readonly categories = this._categories.asReadonly();
  readonly searchResults = this._searchResults.asReadonly();
  readonly loadingModalVisible = this._loadingModalVisible.asReadonly();
  readonly lockModalVisible = this._lockModalVisible.asReadonly();
  readonly isModuleEditorLoaded = this._isModuleEditorLoaded.asReadonly();
  readonly selectedCategory = this._selectedCategory.asReadonly();
  readonly selectedProduct = this._selectedProduct.asReadonly();
  readonly globalFinishOptionsCache = this._globalFinishOptionsCache.asReadonly();
  readonly globalFinishRulesCache = this._globalFinishRulesCache.asReadonly();
  readonly finishOptionsLoadingState = this._finishOptionsLoadingState.asReadonly();
  readonly hidePrices = this._hidePrices.asReadonly();

  // Computed values
  readonly isHomeState = computed(() => {
    const state = this._state();
    return [
      AppState.HOME_DREAMBOOKSPRO,
      AppState.HOME_ONELABPRO,
      AppState.HOME_LFMPRO
    ].includes(state);
  });

  readonly isDreambooksState = computed(() => {
    const state = this._state();
    return [
      AppState.HOME_DREAMBOOKSPRO,
      AppState.ALBUNS,
      AppState.BOXES,
      AppState.PARENTS,
      AppState.PROOFS,
      AppState.DVDS,
      AppState.GUEST,
      AppState.STUDIO_BOX,
      AppState.BOOKBOX,
      AppState.SIGNATURES,
      AppState.SHOWCASE,
      AppState.SHOWCASE_COVERS,
      AppState.ANALOG_ALBUNS
    ].includes(state);
  });

  readonly isOnelabState = computed(() => {
    const state = this._state();
    return [
      AppState.HOME_ONELABPRO,
      AppState.GIFTS,
      AppState.CALENDARS,
      AppState.DECOR,
      AppState.RIGIDS,
      AppState.FLEXIBLES,
      AppState.PHOTOS_POSTERS,
      AppState.GREETINGS,
      AppState.OFFSET,
      AppState.CARDS,
      AppState.FRAMES,
      AppState.FILMROLL,
      AppState.SHOWCASE_ONELAB
    ].includes(state);
  });

  readonly isLfmState = computed(() => {
    const state = this._state();
    return [
      AppState.HOME_LFMPRO,
      AppState.LFMPRODUCTS
    ].includes(state);
  });

  // Setters
  setState(state: AppState): void {
    this._previousState.set(this._state());
    this._state.set(state);
  }

  setCountries(countries: Country[]): void {
    this._countries.set(this.localizeCountryNames(countries, this.getLocale()));
  }

  setLocale(locale: string): void {
    // Keep both Dreambooks packages aligned with the canonical route locale.
    // Their bootstrap configs are only defaults and otherwise remained pt_PT
    // after an in-app language change (notably in the public guest renderer).
    this.dreambooksApiConfig.locale = locale;
    this.dreambooksConfiguration.setLocale(locale);
    this.rendererService.patchConfig({ locale });

    if (this._locale() !== locale) {
      this._locale.set(locale);
      this._countries.update(countries => this.localizeCountryNames(countries, locale));
      this.clearFinishOptionsCache();
      return;
    }
    this._locale.set(locale);
  }

  private localizeCountryNames(countries: Country[], locale: string): Country[] {
    if (countries.length === 0) return countries;

    try {
      const regionNames = new Intl.DisplayNames([locale.replace('_', '-')], { type: 'region' });
      return countries.map(country => ({
        ...country,
        name: regionNames.of(country.code.toUpperCase()) || country.name
      }));
    } catch {
      return countries;
    }
  }

  getLocale(): string {
    return this._locale() || 'pt_PT';
  }

  buildLocaleCommands(commands: Array<string | number> = []): Array<string | number> {
    return ['/', this.getLocale(), ...commands];
  }

  buildLocaleUrl(url: string): string {
    const locale = this.getLocale();
    if (!url) {
      return `/${locale}`;
    }

    const [pathPart, queryPart] = url.split('?');
    const normalizedPath = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;

    if (normalizedPath === `/${locale}` || normalizedPath.startsWith(`/${locale}/`)) {
      return queryPart ? `${normalizedPath}?${queryPart}` : normalizedPath;
    }

    const prefixedPath = normalizedPath === '/' ? `/${locale}` : `/${locale}${normalizedPath}`;
    return queryPart ? `${prefixedPath}?${queryPart}` : prefixedPath;
  }

  /**
   * Build the public guest preview link for a share access token. The guest
   * preview app is deployed on its own host (environment.previewBaseUrl —
   * dev: preview.express.dev…, prod: editorpreview.com); falls back to the
   * current origin when unset (local dev). Path is the locale-prefixed
   * `/share/<token>` guest route.
   */
  buildGuestPreviewUrl(accessToken: string, locale = this.getLocale()): string {
    const base = environment.previewBaseUrl || window.location.origin;
    return `${base.replace(/\/$/, '')}/${locale}/share/${accessToken}`;
  }

  setServerPing(value: boolean): void {
    this._serverPing.set(value);
  }

  setPartner(partner: PartnerInfo | null): void {
    this._partner.set(partner);
  }

  setUploading(value: boolean): void {
    this._uploading.set(value);
  }

  setCategories(categories: Category[]): void {
    this._categories.set(categories);
  }

  setSearchResults(results: ProductInfo[]): void {
    this._searchResults.set(results);
  }

  setLoadingModalVisible(visible: boolean): void {
    this._loadingModalVisible.set(visible);
  }

  setLockModalVisible(visible: boolean): void {
    this._lockModalVisible.set(visible);
  }

  setModuleEditorLoaded(loaded: boolean): void {
    this._isModuleEditorLoaded.set(loaded);
    if (loaded) {
      this._loadingModalVisible.set(false);
    }
  }

  goBack(): void {
    const prev = this._previousState();
    if (prev) {
      this.setState(prev);
    }
  }

  clearSearch(): void {
    this._searchResults.set([]);
  }

  setSelectedCategory(category: string | null): void {
    this._selectedCategory.set(category);
  }

  setSelectedProduct(product: ProductInfo | null): void {
    this._selectedProduct.set(product);
  }

  toggleHidePrices(): void {
    this._hidePrices.update(v => !v);
    this.persistHidePrices();
  }

  /** Reset the manual hide-prices toggle — called on logout so the state
   * can't leak into the next session (whose user may have no toggle, e.g.
   * sub-users). Clears the persisted value too. */
  resetHidePrices(): void {
    this._hidePrices.set(false);
    this.persistHidePrices();
  }

  private persistHidePrices(): void {
    try {
      if (this._hidePrices()) {
        localStorage.setItem(AppStateService.HIDE_PRICES_KEY, '1');
      } else {
        localStorage.removeItem(AppStateService.HIDE_PRICES_KEY);
      }
    } catch {
      // Storage unavailable — the in-memory toggle still applies this session.
    }
  }

  // Global finish options cache management

  /**
   * Check if finish options are cached for a product
   */
  hasFinishOptions(productId: number): boolean {
    return this._globalFinishOptionsCache().has(productId);
  }

  /**
   * Get cached finish options for a product
   */
  getFinishOptions(productId: number): FinishOptionInfo[] | undefined {
    return this._globalFinishOptionsCache().get(productId);
  }

  /**
   * Get cached finish rules for a product
   */
  getFinishRules(productId: number): FinishRuleInfo[] | undefined {
    return this._globalFinishRulesCache().get(productId);
  }

  /**
   * Get loading state for a product's finish options
   */
  getFinishOptionsLoadingState(productId: number): 'loading' | 'loaded' | 'error' | undefined {
    return this._finishOptionsLoadingState().get(productId);
  }

  /**
   * Cache finish options and rules for a product
   */
  cacheFinishOptions(productId: number, options: FinishOptionInfo[], rules: FinishRuleInfo[] = []): void {
    const optionsCache = new Map(this._globalFinishOptionsCache());
    const rulesCache = new Map(this._globalFinishRulesCache());
    const loadingState = new Map(this._finishOptionsLoadingState());

    optionsCache.set(productId, options);
    rulesCache.set(productId, rules);
    loadingState.set(productId, 'loaded');

    this._globalFinishOptionsCache.set(optionsCache);
    this._globalFinishRulesCache.set(rulesCache);
    this._finishOptionsLoadingState.set(loadingState);

    console.log(`[AppStateService] Cached finish options for product ${productId}: ${options.length} options, ${rules.length} rules`);
  }

  /**
   * Set loading state for a product's finish options
   */
  setFinishOptionsLoading(productId: number): void {
    const loadingState = new Map(this._finishOptionsLoadingState());
    loadingState.set(productId, 'loading');
    this._finishOptionsLoadingState.set(loadingState);
  }

  /**
   * Set error state for a product's finish options
   */
  setFinishOptionsError(productId: number): void {
    const loadingState = new Map(this._finishOptionsLoadingState());
    loadingState.set(productId, 'error');
    this._finishOptionsLoadingState.set(loadingState);
  }

  /**
   * Clear all finish options cache
   */
  clearFinishOptionsCache(): void {
    this._globalFinishOptionsCache.set(new Map());
    this._globalFinishRulesCache.set(new Map());
    this._finishOptionsLoadingState.set(new Map());
    console.log('[AppStateService] Cleared all finish options cache');
  }

  /**
   * Clear finish options cache for a specific product
   */
  clearProductFinishOptions(productId: number): void {
    const optionsCache = new Map(this._globalFinishOptionsCache());
    const rulesCache = new Map(this._globalFinishRulesCache());
    const loadingState = new Map(this._finishOptionsLoadingState());

    optionsCache.delete(productId);
    rulesCache.delete(productId);
    loadingState.delete(productId);

    this._globalFinishOptionsCache.set(optionsCache);
    this._globalFinishRulesCache.set(rulesCache);
    this._finishOptionsLoadingState.set(loadingState);

    console.log(`[AppStateService] Cleared finish options cache for product ${productId}`);
  }
}
